import type { ApiClient } from '../../api';
import { ExportLeiste } from '../../muster';

/**
 * Das Brandschottkataster als PDF und als Excel (AK1, 19.10.2026).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ZWEI FORMEN EINES BLATTS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Das PDF (BRS-SKA) geht auf die Baustelle und ins Register. Die Excel-Liste
 * hat dieselben Zeilen und denselben Datenhash; sie ist zum Filtern, Sortieren
 * und Weiterverarbeiten da und trägt keine eigene Nummer (TX-STANDARD S2.5).
 * Deshalb stehen beide Knöpfe nebeneinander, PDF zuerst (S1.9).
 *
 * Bis AK1 war das ein eigener Knopf in der Brandschottkette. Jetzt ist es die
 * Exportleiste des Standards: „wird erstellt …“ und gesperrt während des
 * Erstellens, ein Fehler als ErrorState mit der Meldung des Servers (z. B.
 * „Es gibt noch keine vergebene Schottnummer …“), Fertig über aria-live.
 *
 * `gesperrt`: Während die Brandschottkette rechnet, gibt es kein Kataster -
 * genau wie vorher, als derselbe Zustand den PDF-Knopf sperrte.
 *
 * `beimLaden`: Umgekehrt sperrt die Kette sich, solange ein Kataster entsteht
 * (vorher teilten sich beide Knöpfe einen Zustand). Die Leiste meldet Beginn
 * und Ende, auch bei einem Fehler.
 */
export function KatasterExport({ api, projektId, gesperrt, beimLaden }: {
  api: ApiClient;
  projektId: string;
  gesperrt?: string;
  beimLaden?: (laeuft: boolean) => void;
}) {
  const sperre = gesperrt ? { grund: gesperrt } : undefined;

  const gemeldet = (laden: () => Promise<void>) => async () => {
    beimLaden?.(true);
    try {
      await laden();
    } finally {
      beimLaden?.(false);
    }
  };

  return (
    <ExportLeiste
      ziele={[
        {
          art: 'pdf',
          beschriftung: 'Kataster als PDF',
          laden: gemeldet(() => api.katasterPdf(projektId)),
          gesperrt: sperre,
        },
        {
          art: 'excel',
          beschriftung: 'Kataster als Excel',
          laden: gemeldet(() => api.katasterExcel(projektId)),
          gesperrt: sperre,
        },
      ]}
    />
  );
}
